import axios, { AxiosError } from 'axios';
import apiClient from './apiClient';
import { GenesisBusinessBrief } from '../types/genesisBusinessBrief';
import { mapGenesisResponseToBrief } from '../utils/genesisMapper';

// Configuration
const API_BASE_URL = '/api/v1/genesis';
const GENERATION_TIMEOUT_MS = 130000; // 130 seconds (2 min + marge)

// Error Types
export type GenesisErrorType = 'QUOTA_EXCEEDED' | 'BRIEF_EXPIRED' | 'TIMEOUT' | 'TECHNICAL_ERROR' | 'UNAUTHORIZED';

export interface GenesisError {
    type: GenesisErrorType;
    message: string;
    details?: any;
}

// Types pour les payloads
export interface BusinessInfoPayload {
    company_name: string;
    industry: string;
    company_size: string;
    description: string;
}

export interface MarketInfoPayload {
    target_audience: string;
    competitors: string[];
    market_challenges: string;
    goals: string[];
}

// API Service
export const genesisApi = {
    /**
     * Triggers the generation of a Business Brief.
     * @param businessInfo Data from step 1 (BusinessInfoStep)
     * @param marketInfo Data from step 2 (MarketInfoStep)
     * @returns The generated brief mapped to frontend format.
     */
    generateBusinessBrief: async (
        businessInfo: BusinessInfoPayload,
        marketInfo: MarketInfoPayload
    ): Promise<GenesisBusinessBrief> => {
        try {
            const payload = {
                business_info: businessInfo,
                market_info: marketInfo
            };

            const response = await apiClient.post(
                `${API_BASE_URL}/generate-brief/`,
                payload,
                { timeout: GENERATION_TIMEOUT_MS }
            );

            // Mapper la réponse Genesis vers le format frontend
            return mapGenesisResponseToBrief(response.data, businessInfo, marketInfo);
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Retrieves a Business Brief by ID.
     * @param briefId The ID of the brief to fetch.
     * @returns The full Business Brief object.
     */
    getBusinessBrief: async (briefId: string): Promise<GenesisBusinessBrief> => {
        try {
            const response = await apiClient.get(`${API_BASE_URL}/business-brief/${briefId}/`);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Checks the user's quota status.
     * @param userId The user ID.
     * @returns Quota status object.
     */
    getQuotaStatus: async (userId: number) => {
        try {
            const response = await apiClient.get(`${API_BASE_URL}/users/${userId}/subscription/`);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }
};

// Helper to map Axios errors to GenesisError
const handleApiError = (error: any): GenesisError => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<any>;

        // Timeout
        if (axiosError.code === 'ECONNABORTED') {
            return {
                type: 'TIMEOUT',
                message: 'La génération prend plus de temps que prévu. Veuillez réessayer.',
            };
        }

        // HTTP Errors
        if (axiosError.response) {
            const status = axiosError.response.status;
            const data = axiosError.response.data;

            if (status === 403) {
                // Check if it's a quota issue
                if (data?.error === 'QUOTA_EXCEEDED' || data?.error === 'NO_SUBSCRIPTION') {
                    return {
                        type: 'QUOTA_EXCEEDED',
                        message: data?.message || 'Quota dépassé ou abonnement invalide.',
                        details: data
                    };
                }
                return { type: 'UNAUTHORIZED', message: 'Accès refusé.' };
            }

            if (status === 404) {
                if (data?.error === 'BRIEF_EXPIRED') {
                    return {
                        type: 'BRIEF_EXPIRED',
                        message: 'Ce brief a expiré.',
                        details: data
                    };
                }
                return { type: 'TECHNICAL_ERROR', message: 'Ressource introuvable.' };
            }

            if (status === 401) {
                return { type: 'UNAUTHORIZED', message: 'Non authentifié.' };
            }

            return {
                type: 'TECHNICAL_ERROR',
                message: data?.message || 'Une erreur technique est survenue.',
                details: data
            };
        }
    }

    // Network or other errors
    return {
        type: 'TECHNICAL_ERROR',
        message: 'Erreur de connexion. Veuillez vérifier votre réseau.',
    };
};
